(() => {
  const DEFAULT_PAGE_SIZE = 6;
  const REQUIRED_CLASSES = 2;
  const MAX_BODY_LENGTH = 1000;
  const STUDENT_ENDPOINT = "/apps/studio-danza/opiniones";
  const APPROVED_STATUS = "APPROVED";

  const toFiniteNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const clamp = (value, min, max, fallback = min) =>
    Math.min(max, Math.max(min, toFiniteNumber(value, fallback)));

  const clampRating = (value) =>
    Math.round(clamp(value, 0, 5, 0));

  const ratingLabel = (rating) => {
    const value = clampRating(rating);
    return value === 0
      ? "Sin estrellas"
      : `${value} ${value === 1 ? "estrella" : "estrellas"} de 5`;
  };

  const starString = (rating) => {
    const value = clampRating(rating);
    return `${"★".repeat(value)}${"☆".repeat(5 - value)}`;
  };

  const nextRating = (current, clicked) => {
    const currentValue = clampRating(current);
    const clickedValue = clampRating(clicked);
    return currentValue === clickedValue ? 0 : clickedValue;
  };

  const progressLabel = (completed) =>
    `${clamp(completed, 0, REQUIRED_CLASSES, 0)}/${REQUIRED_CLASSES}`;

  const statusLabel = (status) => ({
    PENDING: "En revisión",
    APPROVED: "Publicada",
    REJECTED: "Rechazada",
  }[String(status || "").toUpperCase()] || "Sin opinión");

  const normalizeReview = (review) => {
    if (!review || typeof review !== "object") return null;
    const id = typeof review.id === "string" ? review.id : "";
    const body = typeof review.body === "string" ? review.body : "";
    const status = String(review.status || "PENDING").toUpperCase();
    return {
      id,
      rating: clampRating(review.rating),
      body,
      status,
      createdAt: review.createdAt || "",
      updatedAt: review.updatedAt || "",
    };
  };

  const normalizeStudentCourse = (row) => {
    if (!row || typeof row !== "object") return null;
    const nestedCourse = row.course && typeof row.course === "object" ? row.course : {};
    const progress = row.progress && typeof row.progress === "object" ? row.progress : {};
    const review = normalizeReview(row.review || row.opinion || row.existingReview);
    const completedClasses = clamp(
      row.completedClasses ?? progress.completedClasses ?? progress.completed ?? row.classesCompleted,
      0,
      REQUIRED_CLASSES,
      0,
    );
    const eligible = row.eligible === true || progress.eligible === true || completedClasses >= REQUIRED_CLASSES;
    const courseId = row.courseId || nestedCourse.id || row.id;
    if (courseId == null || String(courseId).trim() === "") return null;
    return {
      courseId: String(courseId),
      courseName: String(row.courseName || row.name || nestedCourse.name || "Curso"),
      completedClasses,
      eligible,
      review,
    };
  };

  const normalizeStudentCourses = (payload) => {
    const rows = Array.isArray(payload?.courses)
      ? payload.courses
      : Array.isArray(payload?.opinions)
        ? payload.opinions
        : [];
    return rows.map(normalizeStudentCourse).filter(Boolean);
  };

  const normalizePublicReview = (row) => {
    if (!row || typeof row !== "object") return null;
    const status = row.status == null ? APPROVED_STATUS : String(row.status).toUpperCase();
    if (status !== APPROVED_STATUS) return null;
    const displayName = typeof row.displayName === "string"
      ? row.displayName
      : typeof row.author === "string"
        ? row.author
        : "Alumno";
    const initials = typeof row.initials === "string"
      ? row.initials
      : typeof row.authorInitials === "string"
        ? row.authorInitials
        : "";
    return {
      displayName,
      initials,
      courseName: typeof row.courseName === "string" ? row.courseName : "",
      body: typeof row.body === "string" ? row.body : "",
      rating: clampRating(row.rating),
      approvedAt: row.approvedAt || row.createdAt || "",
    };
  };

  const normalizePublicReviews = (payload) => {
    const rows = Array.isArray(payload?.reviews)
      ? payload.reviews
      : Array.isArray(payload?.opinions)
        ? payload.opinions
        : [];
    return rows.map(normalizePublicReview).filter(Boolean);
  };

  const normalizePagination = (payload, reviewCount, fallbackPageSize = DEFAULT_PAGE_SIZE) => {
    const source = payload?.pagination && typeof payload.pagination === "object"
      ? payload.pagination
      : {};
    const pageSize = clamp(
      source.pageSize ?? payload?.pageSize ?? fallbackPageSize,
      1,
      50,
      fallbackPageSize,
    );
    const page = Math.max(1, Math.floor(toFiniteNumber(source.page ?? payload?.page, 1)));
    const total = Math.max(0, Math.floor(toFiniteNumber(
      source.total ?? payload?.total ?? payload?.totalCount,
      reviewCount,
    )));
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    return {
      page: Math.min(page, totalPages),
      pageSize,
      total,
      totalPages,
      hasPrevious: source.hasPrevious === true || page > 1,
      hasNext: source.hasNext === true || source.hasNextPage === true || page < totalPages,
    };
  };

  const publicPayloadVisible = (payload, reviews = normalizePublicReviews(payload)) =>
    payload?.visible !== false && payload?.enabled !== false && payload?.reviewsEnabled !== false && reviews.length > 0;

  const requestJson = async (url, options = {}) => {
    const headers = new Headers(options.headers || {});
    headers.set("Accept", "application/json");
    if (options.body != null && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const response = await fetch(url, {
      ...options,
      credentials: "same-origin",
      cache: "no-store",
      headers,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error || payload?.message || "No fue posible completar la solicitud.");
    }
    return payload;
  };

  const clearChildren = (node) => {
    if (!node) return;
    while (node.firstChild) node.removeChild(node.firstChild);
  };

  const createElement = (tagName, className, text) => {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  };

  const createStars = (rating, className = "sd-review-stars") => {
    const stars = createElement("span", className, starString(rating));
    stars.setAttribute("role", "img");
    stars.setAttribute("aria-label", ratingLabel(rating));
    return stars;
  };

  const openDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === "function") dialog.close();
    else dialog.removeAttribute("open");
  };

  const renderStudentReviews = ({ panel, rows, loading, empty, error, list, tabCount }) => {
    if (!panel || !list) return;
    clearChildren(list);
    if (loading) loading.hidden = false;
    if (error) error.hidden = true;
    if (empty) empty.hidden = true;
    if (tabCount) {
      const count = rows.filter((row) => row.eligible || row.review).length;
      tabCount.textContent = count > 0 ? String(count) : "";
      tabCount.hidden = count === 0;
    }
    rows.forEach((row) => {
      const card = createElement("article", "sd-review-student-card");
      card.dataset.reviewCourseId = row.courseId;

      const header = createElement("div", "sd-review-student-card__header");
      const heading = createElement("div", "sd-review-student-card__heading");
      heading.appendChild(createElement("p", "sd-review-eyebrow", "Curso"));
      heading.appendChild(createElement("h3", "", row.courseName));
      header.appendChild(heading);

      const progress = createElement("span", "sd-review-progress");
      progress.textContent = row.eligible ? "Disponible" : `Progreso ${progressLabel(row.completedClasses)}`;
      progress.setAttribute("aria-label", row.eligible ? "Opinión disponible" : `Progreso ${progressLabel(row.completedClasses)} para habilitar la opinión`);
      header.appendChild(progress);
      card.appendChild(header);

      const meta = createElement("div", "sd-review-student-card__meta");
      const reviewStatus = createElement("span", "sd-review-status", statusLabel(row.review?.status));
      reviewStatus.dataset.reviewStatus = row.review?.status || "NONE";
      meta.appendChild(reviewStatus);
      if (row.review) meta.appendChild(createStars(row.review.rating));
      else meta.appendChild(createElement("span", "sd-review-unrated", "Aún no has calificado"));
      card.appendChild(meta);

      const action = createElement("button", "sd-button sd-button--primary sd-review-write");
      action.type = "button";
      action.dataset.reviewOpen = row.courseId;
      action.textContent = row.review ? "Editar opinión" : row.eligible ? "Escribir opinión" : "Disponible después de 2 clases";
      action.disabled = !row.eligible && !row.review;
      if (action.disabled) action.setAttribute("aria-disabled", "true");
      card.appendChild(action);
      list.appendChild(card);
    });
    if (loading) loading.hidden = true;
    list.hidden = rows.length === 0;
    if (empty) empty.hidden = rows.length > 0;
  };

  const initStudentReviews = (panel) => {
    const endpoint = panel.dataset.reviewsEndpoint || STUDENT_ENDPOINT;
    const list = panel.querySelector("[data-reviews-list]");
    const loading = panel.querySelector("[data-reviews-loading]");
    const empty = panel.querySelector("[data-reviews-empty]");
    const error = panel.querySelector("[data-reviews-error]");
    const status = panel.querySelector("[data-reviews-status]");
    const retry = panel.querySelector("[data-reviews-retry]");
    const tabCount = document.querySelector("[data-reviews-count]");
    const modal = document.querySelector("[data-review-modal]");
    const form = modal?.querySelector("[data-review-form]");
    const modalTitle = modal?.querySelector("[data-review-modal-title]");
    const modalCourse = modal?.querySelector("[data-review-modal-course]");
    const modalStatus = modal?.querySelector("[data-review-form-status]");
    const modalSubmit = modal?.querySelector("[data-review-submit]");
    const textarea = modal?.querySelector("[data-review-body]");
    const ratingOptions = modal?.querySelector("[data-review-rating-options]");
    let rows = [];
    let activeCourse = null;
    let pendingRealtimeRefresh = false;
    let loadVersion = 0;
    let reviewsLoaded = false;

    const setStatus = (message, state = "") => {
      if (!modalStatus) return;
      modalStatus.textContent = message;
      if (state) modalStatus.dataset.reviewFormStatusState = state;
      else delete modalStatus.dataset.reviewFormStatusState;
    };

    const updateRatingChoice = () => {
      if (!ratingOptions) return;
      const selectedRating = clampRating(ratingOptions.dataset.rating);
      ratingOptions?.querySelectorAll("[data-review-rating-option]").forEach((label) => {
        const value = clampRating(label.dataset.rating);
        const selected = value <= selectedRating;
        label.dataset.selected = String(selected);
        label.setAttribute("aria-checked", String(value === selectedRating && selectedRating > 0));
      });
    };

    const buildRatingOptions = () => {
      if (!ratingOptions) return;
      if (ratingOptions.childElementCount === 0) {
        const starPath = "M12 2.5l2.94 5.95 6.56.95-4.75 4.63 1.12 6.54L12 17.48l-5.87 3.09 1.12-6.54-4.75-4.63 6.56-.95L12 2.5z";
        for (let value = 1; value <= 5; value += 1) {
          const label = createElement("button", "sd-review-rating-option");
          label.type = "button";
          label.dataset.reviewRatingOption = "true";
          label.dataset.rating = String(value);
          label.setAttribute("role", "radio");
          label.setAttribute("aria-label", `${value} ${value === 1 ? "estrella" : "estrellas"}`);
          label.setAttribute("aria-checked", "false");
          label.setAttribute("aria-posinset", String(value));
          label.setAttribute("aria-setsize", "5");

          const visual = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          visual.classList.add("sd-review-rating-star");
          visual.setAttribute("viewBox", "0 0 24 24");
          visual.setAttribute("focusable", "false");
          visual.setAttribute("aria-hidden", "true");
          const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
          path.setAttribute("d", starPath);
          visual.appendChild(path);
          label.appendChild(visual);
          ratingOptions.appendChild(label);

          label.addEventListener("click", () => {
            const current = ratingOptions.dataset.rating;
            ratingOptions.dataset.rating = String(nextRating(current, value));
            ratingOptions.dataset.ratingTouched = "true";
            updateRatingChoice();
          });
        }
      }
      if (!("rating" in ratingOptions.dataset)) ratingOptions.dataset.rating = "";
      if (!("ratingTouched" in ratingOptions.dataset)) ratingOptions.dataset.ratingTouched = "false";
    };

    const openReview = (course) => {
      if (!course || (!course.eligible && !course.review)) return;
      activeCourse = course;
      buildRatingOptions();
      if (modalTitle) modalTitle.textContent = course.review ? "Editar tu opinión" : "Comparte tu opinión";
      if (modalCourse) modalCourse.textContent = course.courseName;
      if (textarea) textarea.value = course.review?.body || "";
      if (ratingOptions) {
        ratingOptions.dataset.rating = course.review ? String(clampRating(course.review.rating)) : "";
        ratingOptions.dataset.ratingTouched = course.review ? "true" : "false";
      }
      updateRatingChoice();
      setStatus("");
      openDialog(modal);
      const focusTarget = course.review?.rating
        ? ratingOptions?.querySelector(`[data-review-rating-option][data-rating="${clampRating(course.review.rating)}"]`)
        : textarea;
      window.setTimeout(() => focusTarget?.focus(), 0);
    };

    const render = () => renderStudentReviews({ panel, rows, loading, empty, error, list, tabCount });

    const load = async ({ quiet = false } = {}) => {
      if (!list) return;
      const requestVersion = ++loadVersion;
      if (!quiet) {
        if (loading) loading.hidden = false;
        if (error) error.hidden = true;
        if (status) status.textContent = "Cargando tus opiniones…";
      }
      try {
        const payload = await requestJson(endpoint);
        if (requestVersion !== loadVersion) return;
        reviewsLoaded = true;
        rows = normalizeStudentCourses(payload);
        render();
        if (status) status.textContent = rows.length ? "" : "Aún no tienes cursos disponibles para opinar.";
      } catch (reason) {
        if (requestVersion !== loadVersion || (quiet && reviewsLoaded)) return;
        rows = [];
        render();
        if (empty) empty.hidden = true;
        if (error) error.hidden = false;
        if (status) status.textContent = "";
      }
    };
    const refreshAfterModal = () => {
      if (!pendingRealtimeRefresh) return;
      pendingRealtimeRefresh = false;
      void load({ quiet: true });
    };
    const refreshStudentReviews = () => {
      if (modal?.open) { pendingRealtimeRefresh = true; return; }
      void load({ quiet: true });
    };
    modal?.addEventListener("close", refreshAfterModal);
    document.addEventListener("studio-danza:student-space-refresh", refreshStudentReviews);

    list?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-review-open]");
      if (!button || button.disabled) return;
      const course = rows.find((row) => row.courseId === button.dataset.reviewOpen);
      openReview(course);
    });
    retry?.addEventListener("click", () => void load());

    modal?.querySelectorAll("[data-review-modal-close]").forEach((button) => {
      button.addEventListener("click", () => closeDialog(modal));
    });
    modal?.addEventListener("click", (event) => {
      if (event.target === modal) closeDialog(modal);
    });
    modal?.addEventListener("cancel", () => closeDialog(modal));

    form?.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!activeCourse || !modalSubmit || !textarea) return;
      const ratingValue = ratingOptions?.dataset.rating;
      const ratingTouched = ratingOptions?.dataset.ratingTouched === "true";
      const body = textarea.value.trim();
      if (!ratingTouched || ratingValue == null || ratingValue === "") {
        setStatus("Selecciona una calificación de 0 a 5 estrellas.", "error");
        return;
      }
      if (!body) {
        setStatus("Escribe un comentario antes de enviar.", "error");
        textarea.focus();
        return;
      }
      if (body.length > MAX_BODY_LENGTH) {
        setStatus(`El comentario no puede superar ${MAX_BODY_LENGTH} caracteres.`, "error");
        textarea.focus();
        return;
      }
      modalSubmit.disabled = true;
      setStatus("Guardando tu opinión…");
      const reviewId = activeCourse.review?.id;
      const url = reviewId
        ? `${endpoint}/${encodeURIComponent(reviewId)}`
        : endpoint;
      try {
        const payload = await requestJson(url, {
          method: reviewId ? "PATCH" : "POST",
          body: JSON.stringify({
            courseId: activeCourse.courseId,
            rating: clampRating(ratingValue),
            body,
          }),
        });
        const saved = normalizeReview(payload?.review || payload?.opinion || payload);
        activeCourse.review = saved || {
          id: reviewId || "local-review",
          rating: clampRating(ratingValue),
          body,
          status: "PENDING",
        };
        if (ratingOptions) {
          ratingOptions.dataset.rating = String(activeCourse.review.rating);
          ratingOptions.dataset.ratingTouched = "true";
        }
        updateRatingChoice();
        rows = rows.map((row) => row.courseId === activeCourse.courseId ? { ...row, review: activeCourse.review } : row);
        render();
        setStatus("Tu opinión fue enviada y está pendiente de moderación.", "success");
      } catch (reason) {
        setStatus(reason instanceof Error ? reason.message : "No se pudo guardar tu opinión. Intenta nuevamente.", "error");
      } finally {
        modalSubmit.disabled = false;
      }
    });

    buildRatingOptions();
    void load();
  };

  const renderPublicSummary = (section, payload, reviews) => {
    const summaryNode = section.querySelector("[data-reviews-summary]");
    if (!summaryNode) return;
    const summary = payload?.summary && typeof payload.summary === "object" ? payload.summary : {};
    const total = Math.max(0, Math.floor(toFiniteNumber(summary.total ?? payload?.total, reviews.length)));
    const average = Number.isFinite(Number(summary.average))
      ? Number(summary.average).toFixed(1)
      : reviews.length
        ? (reviews.reduce((totalValue, review) => totalValue + review.rating, 0) / reviews.length).toFixed(1)
        : "0.0";
    summaryNode.textContent = `${average} de 5 · ${total} ${total === 1 ? "opinión verificada" : "opiniones verificadas"}`;
  };

  const renderPublicCard = (review) => {
    const figure = createElement("figure", "sd-review-public-card");
    const header = createElement("div", "sd-review-public-card__header");
    header.appendChild(createStars(review.rating, "sd-review-stars sd-review-stars--public"));
    figure.appendChild(header);

    const quote = createElement("blockquote", "sd-review-public-card__quote", review.body);
    figure.appendChild(quote);

    const caption = createElement("figcaption", "sd-review-public-card__caption");
    caption.appendChild(createElement("span", "sd-review-avatar", review.initials || "•"));
    const author = createElement("div", "sd-review-public-card__author");
    author.appendChild(createElement("strong", "", review.displayName));
    if (review.courseName) author.appendChild(createElement("small", "", review.courseName));
    caption.appendChild(author);
    figure.appendChild(caption);
    return figure;
  };

  const renderPublicPagination = (section, endpoint, pagination, loadPage) => {
    const node = section.querySelector("[data-reviews-pagination]");
    if (!node) return;
    clearChildren(node);
    if (pagination.totalPages <= 1) {
      node.hidden = true;
      return;
    }
    node.hidden = false;
    const previous = createElement("button", "sd-review-page-button", "Anterior");
    previous.type = "button";
    previous.disabled = !pagination.hasPrevious;
    previous.setAttribute("aria-label", "Ver opiniones anteriores");
    previous.addEventListener("click", () => {
      if (pagination.hasPrevious) void loadPage(pagination.page - 1);
    });
    const pageStatus = createElement("span", "sd-review-page-status", `Página ${pagination.page} de ${pagination.totalPages}`);
    pageStatus.setAttribute("aria-live", "polite");
    const next = createElement("button", "sd-review-page-button", "Siguiente");
    next.type = "button";
    next.disabled = !pagination.hasNext;
    next.setAttribute("aria-label", "Ver más opiniones");
    next.addEventListener("click", () => {
      if (pagination.hasNext) void loadPage(pagination.page + 1);
    });
    node.append(previous, pageStatus, next);
  };

  const initPublicReviews = (section) => {
    const endpoint = section.dataset.reviewsEndpoint;
    const list = section.querySelector("[data-reviews-list]");
    const status = section.querySelector("[data-reviews-status]");
    if (!endpoint || !list) return;

    const loadPage = async (page) => {
      const url = new URL(endpoint, window.location.href);
      url.searchParams.set("page", String(Math.max(1, page)));
      url.searchParams.set("pageSize", section.dataset.reviewsPageSize || String(DEFAULT_PAGE_SIZE));
      try {
        const payload = await requestJson(`${url.pathname}${url.search}`);
        const reviews = normalizePublicReviews(payload);
        if (!publicPayloadVisible(payload, reviews)) {
          section.hidden = true;
          return;
        }
        clearChildren(list);
        reviews.forEach((review) => list.appendChild(renderPublicCard(review)));
        renderPublicSummary(section, payload, reviews);
        const pagination = normalizePagination(payload, reviews.length, Number(section.dataset.reviewsPageSize || DEFAULT_PAGE_SIZE));
        renderPublicPagination(section, endpoint, pagination, loadPage);
        section.hidden = false;
        if (status) status.textContent = "";
      } catch {
        section.hidden = true;
        if (status) status.textContent = "";
      }
    };

    void loadPage(1);
  };

  const init = () => {
    document.querySelectorAll("[data-reviews-student]").forEach(initStudentReviews);
    document.querySelectorAll("[data-reviews-public]").forEach(initPublicReviews);
  };

  const exposed = {
    clampRating,
    nextRating,
    ratingLabel,
    starString,
    progressLabel,
    statusLabel,
    normalizeStudentCourse,
    normalizeStudentCourses,
    normalizePublicReviews,
    normalizePagination,
    publicPayloadVisible,
  };

  if (typeof window !== "undefined") window.StudioDanzaReviews = Object.freeze(exposed);
  if (typeof document !== "undefined") {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
    else init();
  }
})();
